<script lang="ts" setup>
import { ContentComp, GridComp, InputText, DisplayTextWithRoute, DisplayTick } from '@/components';
import DateComp from '@/components/Basic/DateComp.vue';
import { ActionIconHeader } from '@/components/Grid';
import { GridColumnDefinition, GridColumnOptions, GridDataSource, GridSetup } from '@/components/Grid/Classes';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { useTaskLoader } from '@/composeables';
import { EventEngineDao } from '@/dao';
import { $tm } from '@/locize';
import { EngineEvent } from '@/objects/generated';
import { useEventStoreRef } from '@/stores';
import { computed, onMounted, ref, watch } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import EngineHandlerActions from './EngineHandlerActions.vue';

const { eventID } = useEventStoreRef();
const grid = ref<ComponentExposed<typeof GridComp<EngineEvent>> | null>(null);
const data = ref<EngineEvent[]>([]);

const { AddLoader, LoadState, ClearLoaders } = useTaskLoader();

/**
 * Watch for changes in the eventID. We use this to trigger a reload of the engines after we have performed an action
 */
watch(
    () => data.value,
    (nv) => (nv.some((x) => x.EventID === 0) ? loadEngines() : null),
    { deep: true },
);

/**
 * Load the data
 */
const loadEngines = () => {
    ClearLoaders();
    AddLoader(async () => {
        data.value = await EventEngineDao.GetEngineEvents(eventID.value);
    }, 'enginehandler.loadengines');
};

/**
 * Define all the colums for the grid
 */
const columns = computed<GridColumnOptions<any>[]>(() => {
    let _columns: GridColumnOptions<any>[] = [];

    let namedef = buildColDefs('EngineDetails.Name', 'enginehandler.enginename');
    namedef.maxWidth = 120;
    namedef.minWidth = 120;
    _columns.push(namedef);

    _columns.push(buildColDefsDateTime('ActiveFrom', 'enginehandler.activefrom'));
    _columns.push(buildColDefsDateTime('ActiveTo', 'enginehandler.activeto'));

    let lastLoadeddef = buildColDefsDateTime('LastLoaded', 'enginehandler.lastloaded');
    lastLoadeddef.cellClassRules = [
        {
            className: 'grid-cell-success',
            func: (cell: Date, row: EngineEvent) => {
                let limit = 5 * 60 * 1000; // 5 minutes
                let diff = row.LastLoaded != null ? new Date().getTime() - row.LastLoaded.getTime() : 999999;

                return diff <= limit;
            },
        },
    ];
    _columns.push(lastLoadeddef);

    _columns.push(buildColDefs('EngineDetails.Address', 'enginehandler.address', true, 230));
    _columns.push(buildColDefs('EngineDetails.Filters', 'enginehandler.filters', true, 70, 140));
    _columns.push(buildColDefsTick('EngineDetails.IsOnline', 'enginehandler.isonline'));

    _columns.push(buildColDefsTick('EngineDetails.HasLocalCache', 'enginehandler.localcache', true));
    _columns.push(buildColDefsTick('EngineDetails.HasSignalRDispatch', 'enginehandler.signalr', true));

    // TODO: Add a column for actions
    _columns.push(
        new GridColumnOptions<any>({
            dataField: 'EventID',
            columnHeaderText: $tm('enginehandler.actions'),
            editable: false,
            sortable: false,
            filterable: false,
            resizable: false,
            minWidth: 130,
            maxWidth: 130,
            headerComponent: ActionIconHeader,
            cellRenderer: EngineHandlerActions,
        }),
    );

    return _columns;
});

/**
 * Helper method to build the column definitions
 */
const buildColDefs = (field: string, key: string, resizeable: boolean = false, minWidth: number = 70, maxWidth?: number) => {
    return new GridColumnOptions<any>({
        dataField: field,
        columnHeaderText: $tm(key),
        editable: false,
        sortable: false,
        filterable: false,
        resizable: resizeable,
        minWidth: minWidth,
        maxWidth: maxWidth,
        cellEditor: InputText,
        cellRenderer: DisplayTextWithRoute,
    });
};

/**
 * Helper method to build the column definitions for a datetime
 * @param field
 * @param key
 */
const buildColDefsDateTime = (field: string, key: string) => {
    let coldef = buildColDefs(field, key);
    coldef.cellRenderer = DateComp;
    coldef.cellRendererParams = {
        format: 'DATETIME',
        monospace: false,
        dontshowminvalue: true,
    };
    coldef.minWidth = 170;
    coldef.maxWidth = coldef.minWidth;
    return coldef;
};

/**
 * Will render a tick icon
 * @param field
 * @param key
 * @param dontShowFalse
 */
const buildColDefsTick = (field: string, key: string, dontShowFalse?: boolean) => {
    let coldef = buildColDefs(field, key);
    coldef.cellRenderer = DisplayTick;
    coldef.cellRendererParams = {
        dontShowFalse: dontShowFalse,
    };
    coldef.maxWidth = coldef.minWidth;

    return coldef;
};

/**
 * Define the grid setup
 */
const gridSetup = new GridSetup<EngineEvent>({
    rowIdGetter: (row: EngineEvent) => row.EngineDetails?.Address ?? '',
    datasource: new GridDataSource({
        rowGetter: (p: IGridRowGetterParams<any>) => {
            p?.sort(data.value);
            const slice = data.value?.slice(p.startRow, p.endRow);
            return p.successCallback(slice, data.value, data.value.length);
        },
    }),
    rowSelection: 'multiple',
    GridColumnDefinition: new GridColumnDefinition({ ColumnOptions: columns.value }),
});

onMounted(() => {
    // TODO: Load all the enginesthe event is loaded into
    loadEngines();
});

defineExpose({
    Refresh: () => {
        loadEngines();
    },
    ReloadAllEngines: async () => {
        await EventEngineDao.ReloadEvent(eventID.value);
    },
});
</script>
<template>
    <ContentComp
        :load-state="LoadState"
        :fill-content="true"
        class="p-0"
    >
        <GridComp
            v-if="data.length > 0"
            class="grid-borderless"
            ref="grid"
            :grid-setup="gridSetup"
        />
    </ContentComp>
</template>
